import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, Webhook as WebhookIcon, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { webhookStatsQueryOptions } from '@/lib/webhooks';

/**
 * Dashboard tile for admins with `webhook.manage`. Shows:
 *   - count of active subscriptions
 *   - 7-day delivery success rate (with the actual fail count when nonzero)
 *
 * Hidden by the parent route when the user lacks permission so we don't
 * need to read session here — just fetch and render.
 */
export function WebhookHealthCard() {
  const q = useQuery(webhookStatsQueryOptions);
  const stats = q.data;

  const rateColor =
    !stats || stats.successRate === -1
      ? 'text-slate-400'
      : stats.successRate >= 95
        ? 'text-emerald-600'
        : stats.successRate >= 75
          ? 'text-amber-600'
          : 'text-red-600';

  const rateLabel = !stats || stats.successRate === -1 ? '—' : `${stats.successRate}%`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <WebhookIcon className="text-brand-600 h-4 w-4" />
          Webhooks
        </CardTitle>
        <Link
          to="/webhooks"
          className="flex items-center text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          จัดการ <ArrowRight className="ml-0.5 h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : !stats ? (
          <p className="text-sm text-slate-400">ดึงข้อมูลไม่สำเร็จ</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                {stats.activeWebhooks}
                <span className="ml-1 text-sm font-normal text-slate-400">
                  / {stats.totalWebhooks}
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">Subscription ที่เปิดอยู่</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${rateColor}`}>{rateLabel}</div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                <span>สำเร็จใน {stats.windowDays} วันที่ผ่านมา</span>
              </div>
              {stats.totalAttempts > 0 && (
                <div className="mt-1 flex items-center gap-3 text-[10px]">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    {stats.successCount}
                  </span>
                  {stats.failCount > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle className="h-3 w-3" />
                      {stats.failCount}
                    </span>
                  )}
                </div>
              )}
              {stats.totalAttempts === 0 && (
                <div className="mt-1 text-[10px] text-slate-400">ยังไม่มี delivery</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
